/**
 * US-108 — staff "My Tasks": the caller's actionable stateful task instances
 * (per-assignee + role-pool they can act on). Self-service.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/apiClient";
import type { MyTasksResponse } from "@livingsyncs/types";
import { useFacilityContext } from "./useFacilityContext";
import { invalidateTaskSurfaces } from "./taskSurfaces";

/**
 * Staff "My Tasks". Role-pool (facility-bound) tasks are narrowed to the current
 * facility context — shift-locked when on shift — so a staffer scheduled at A
 * doesn't see facility B's work. Per-assignee tasks (pinned to them) span all
 * their facilities regardless. Mirrors the entity-hook facility-context pattern.
 */
export function useMyTasks(enabled = true) {
  const { currentFacilityId } = useFacilityContext();
  return useQuery({
    queryKey: ["my-tasks", currentFacilityId],
    queryFn: async () => {
      const { data } = await api.get("/me/tasks", {
        params: currentFacilityId
          ? { facility_id: currentFacilityId }
          : undefined,
      });
      return data.data as MyTasksResponse;
    },
    enabled,
  });
}

/** Audited manual check-off of a stateful task. */
export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorFallback: "Could not complete task" },
    mutationFn: async (taskId: string) => {
      const { data } = await api.post(`/tasks/${taskId}/complete`);
      return data.data as { id: string; status: string };
    },
    onSuccess: () => invalidateTaskSurfaces(qc),
  });
}
